// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EditRounded } from '@/constants/hardfin-standard-icons';
import { Button, ButtonProps, TypographyVariant, styled } from '@mui/material';
import {
  ElementType,
  ForwardedRef,
  PropsWithChildren,
  forwardRef,
} from 'react';

interface ButtonContainerProps {
  typographyVariant: TypographyVariant;
  component?: ElementType;
}

function shouldForwardProp(prop: string) {
  return prop !== 'typographyVariant';
}

const ButtonContainer = styled(Button, {
  shouldForwardProp,
})<ButtonContainerProps>(({ theme, typographyVariant }) => ({
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  marginLeft: theme.spacing(-1),
  justifySelf: 'start',
  alignSelf: 'start',
  textAlign: 'left',
  transition: theme.transitions.create('background-color', { duration: 0 }),
  ...theme.typography[typographyVariant],
  '&:hover .edit-icon': {
    opacity: 1,
  },
}));

const Icon = styled(EditRounded)(({ theme }) => ({
  opacity: 0,
  alignSelf: 'start',
  marginTop: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
}));

interface Props extends Pick<ButtonProps, 'onClick' | 'sx'> {
  /**
   * The typography variant of the button content.
   *
   * @default 'body2'
   */
  variant?: TypographyVariant;
  fullWidth?: boolean;

  /**
   * The underlying element to render. Defaults to a native button. Pass "div"
   * when the content may contain interactive elements such as links, since an
   * anchor cannot be nested inside a button. MUI's ButtonBase still applies the
   * button role, focusability, and keyboard handling.
   */
  component?: ElementType;
}

/**
 * Renders an editable property button. This function also forwards a ref to
 * button element.
 */
function EditablePropertyButtonRenderer(
  props: PropsWithChildren<Props>,
  ref: ForwardedRef<HTMLButtonElement>
) {
  const variant = props.variant ?? 'body2';

  return (
    <ButtonContainer
      ref={ref}
      color="inherit"
      component={props.component ?? 'button'}
      onClick={props.onClick}
      sx={props.sx}
      typographyVariant={variant}
      fullWidth={Boolean(props.fullWidth)}
    >
      {props.children}
      <Icon
        className="edit-icon"
        fontSize="inherit"
        color="action"
        aria-label="Edit"
      />
    </ButtonContainer>
  );
}

/**
 * A button that displays children with an edit icon.
 */
export const EditablePropertyButton = forwardRef(
  EditablePropertyButtonRenderer
);
